
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { aiProxy } from '../services/aiProxy';
import { ForgivenessProgram, UserData, DraftApplication } from '../types';
import { ArrowRightIcon } from './icons/ArrowRightIcon';
import { SparkleIcon } from './icons/SparkleIcon';
import { CheckCircleIcon } from './icons/CheckCircleIcon';
import { XCircleIcon } from './icons/XCircleIcon';
import { SpinnerIcon } from './icons/SpinnerIcon';
import { DocumentTextIcon } from './icons/DocumentTextIcon';
import { ShieldIcon } from './icons/ShieldIcon';
import { uploadSecureDocument, saveDraft } from '../services/dbService';

// --- Configuration for Program Requirements ---

export const programRequirements: Record<string, { docs: string[], fields: string[] }> = {
  pslf: {
    docs: ['W-2 Form', 'Pay Stub', 'PSLF Certification Form'],
    fields: ['ssn', 'dob', 'employer_name', 'employer_ein']
  },
  tpd: {
    docs: ['Physician\'s Certification', 'SSA/VA Award Letter'],
    fields: ['ssn', 'dob']
  },
  idr: {
    docs: ['Proof of Income'],
    fields: ['family_size', 'marital_status', 'spouse_agi', 'irs_consent']
  },
};

// Define what fields we should try to extract from each document type
const extractionRules: Record<string, string[]> = {
    'W-2 Form': ['employer_name', 'employer_ein', 'ssn'],
    'PSLF Certification Form': ['employer_name', 'employer_ein', 'ssn', 'dob'],
    'Pay Stub': ['employer_name'],
    'Physician\'s Certification': ['ssn', 'dob'],
    'SSA/VA Award Letter': ['ssn'],
    'Proof of Income': ['spouse_agi'] // Heuristic
};

export const allDocsConfig: Record<string, { name: string; description: string }> = {
    'W-2 Form': { name: 'W-2 Form', description: 'Most recent W-2 from your public service employer.' },
    'Pay Stub': { name: 'Pay Stub', description: 'Most recent pay stub showing full-time employment.' },
    'PSLF Certification Form': { name: 'PSLF Certification Form', description: 'Signed by you and your employer.' },
    'Physician\'s Certification': { name: 'Physician\'s Certification', description: 'A form completed by your doctor.' },
    'SSA/VA Award Letter': { name: 'SSA/VA Award Letter', description: 'If applicable, your disability award letter.' },
    'Proof of Income': { name: 'Proof of Income', description: 'Most recent pay stub or tax return.' },
};

export const allFieldsConfig: Record<string, { label: string; type: string; placeholder?: string, options?: {value: string; label: string}[], required: boolean }> = {
    ssn: { label: 'Social Security Number (SSN)', type: 'text', placeholder: 'XXX-XX-XXXX', required: true },
    dob: { label: 'Date of Birth', type: 'date', required: true },
    employer_name: { label: 'Current Employer Name', type: 'text', placeholder: 'e.g., City of Anytown', required: true },
    employer_ein: { label: 'Employer Identification Number (EIN)', type: 'text', placeholder: 'XX-XXXXXXX', required: true },
    family_size: { label: 'Family Size', type: 'number', placeholder: 'e.g., 4', required: true },
    marital_status: { label: 'Marital Status', type: 'select', options: [{value: 'single', label: 'Single'}, {value: 'married', label: 'Married'}], required: true },
    spouse_agi: { label: "Spouse's Adjusted Gross Income (AGI)", type: 'number', placeholder: 'Enter 0 if not applicable', required: false },
    irs_consent: { label: 'I authorize the Department of Education to retrieve my tax information from the IRS.', type: 'checkbox', required: true }
};

// --- Helper Components & Types ---

type VerificationStatus = 'idle' | 'verifying' | 'uploading' | 'verified' | 'error';
interface UploadedFileState {
  file?: File;
  url?: string;
  status: VerificationStatus;
  error?: string;
  extractedCount?: number;
}

async function fileToGenerativePart(file: File): Promise<{ inlineData: { data: string; mimeType: string } }> {
  const base64EncodedDataPromise = new Promise<string>((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => {
        const result = reader.result;
        // Explicitly check for string to satisfy strict typing
        if (typeof result === 'string') {
            const parts = result.split(',');
            // Ensure we only pass a string to resolve
            const data = parts[1];
            resolve(data || '');
        } else {
            resolve('');
        }
    };
    reader.readAsDataURL(file);
  });
  return {
    inlineData: { data: await base64EncodedDataPromise, mimeType: file.type },
  };
}

// --- Step Components ---

const DocumentUploadStep: React.FC<{ 
    onComplete: (files: Record<string, File>, urls: Record<string, string>) => void; 
    programs: ForgivenessProgram[];
    isTestMode: boolean;
    initialData?: Record<string, string>; // Maps DocName -> URL
    onAutoSave: (uploadedUrls: Record<string, string>) => void;
    onDataExtracted: (data: Record<string, any>) => void;
    userId: string;
    clientId: string;
}> = ({ onComplete, programs, isTestMode, initialData, onAutoSave, onDataExtracted, userId, clientId }) => {
    // Initialize state with pre-existing URLs if they exist
    const [uploadedFiles, setUploadedFiles] = useState<Record<string, UploadedFileState>>(() => {
        const initial: Record<string, UploadedFileState> = {};
        if (initialData) {
            // Fix: Cast url to string to resolve 'unknown' type error during assignment in some TypeScript environments
            Object.entries(initialData).forEach(([name, url]) => {
                initial[name] = { url: url as string, status: 'verified' };
            });
        }
        return initial;
    });
    
    const requiredDocNames = useMemo(() => {
        const docSet = new Set<string>();
        programs.forEach(p => {
            programRequirements[p.id]?.docs.forEach(docName => docSet.add(docName));
        });
        return Array.from(docSet);
    }, [programs]);

    // Fix: Explicitly filter and cast to ensure allDocs elements are valid strings/objects and not unknown or undefined
    const allDocs = requiredDocNames.map(name => allDocsConfig[name]).filter((doc): doc is { name: string; description: string } => !!doc);
    const allDocsVerified = allDocs.length > 0 && allDocs.every(doc => uploadedFiles[doc.name]?.status === 'verified');

    const handleFileUpload = async (docName: string, file: File) => {
      setUploadedFiles(prev => ({
          ...prev,
          [docName]: { file, status: 'verifying' }
      }));

      let extractedInfo: Record<string, any> = {};
      let extractedCount = 0;

      // 1. VERIFY & EXTRACT WITH AI
      if (isTestMode) {
          await new Promise(r => setTimeout(r, 1000)); // Simulate check
      } else {
          try {
              const imagePart = await fileToGenerativePart(file);
              
              const targetFields = extractionRules[docName] || [];
              const fieldsPrompt = targetFields.length > 0 
                ? `Also, attempt to extract the following information if visible: ${targetFields.join(', ')}. Return the response in JSON format with keys: { "isValid": boolean, "reason": string, "extractedData": { ... } }.`
                : `Return JSON format: { "isValid": boolean, "reason": string }.`;

              const prompt = `Analyze this image. Verify if it is a valid ${docName}. ${fieldsPrompt} For dates, use YYYY-MM-DD format. For SSN, standard format.`;
              
              const text = await aiProxy.generateContent({
                  model: 'gemini-1.5-flash',
                  contents: [{
                      role: 'user',
                      parts: [
                          { text: prompt },
                          { inlineData: imagePart.inlineData } as any
                      ]
                  }],
                  config: { responseMimeType: 'application/json' }
              });

              if (!text) throw new Error("No response from AI");

              const result = JSON.parse(text.trim());

              if (!result.isValid) {
                  setUploadedFiles(prev => ({ ...prev, [docName]: { ...prev[docName], status: 'error', error: result.reason || 'Incorrect document type.' } }));
                  return;
              }

              if (result.extractedData) {
                  extractedInfo = result.extractedData;
                  Object.keys(extractedInfo).forEach(key => {
                      if (!extractedInfo[key]) delete extractedInfo[key];
                  });
                  extractedCount = Object.keys(extractedInfo).length;
                  if (extractedCount > 0) {
                      onDataExtracted(extractedInfo);
                  }
              }

          } catch (err) {
              console.error("Verification failed:", err);
              setUploadedFiles(prev => ({ ...prev, [docName]: { ...prev[docName], status: 'error', error: 'Verification failed. Please ensure image is clear.' } }));
              return;
          }
      }

      // 2. UPLOAD TO CLOUD (AUTO-SAVE)
      setUploadedFiles(prev => ({ ...prev, [docName]: { ...prev[docName], status: 'uploading' } }));
      
      try {
          let downloadUrl = '';
          if (isTestMode) {
              downloadUrl = URL.createObjectURL(file); // Fake URL for test mode
          } else {
              // Real Storage Upload
              downloadUrl = await uploadSecureDocument(userId, clientId, file, docName.replace(/\s+/g, '_'));
          }

          // Update Local State
          const newFileState: UploadedFileState = { file, url: downloadUrl, status: 'verified', extractedCount };
          setUploadedFiles(prev => {
              const newState: Record<string, UploadedFileState> = { ...prev, [docName]: newFileState };
              // Trigger Auto-Save Callback
              const allUrls: Record<string, string> = {};
              Object.entries(newState).forEach(([k, v]) => { 
                  if(v.url) allUrls[k] = v.url 
              });
              onAutoSave(allUrls);
              return newState;
          });

      } catch (err) {
          console.error("Upload failed:", err);
          setUploadedFiles(prev => ({ ...prev, [docName]: { ...prev[docName], status: 'error', error: 'Upload failed. Check connection.' } }));
      }
    };

    const handleNext = () => {
        const filesToPass: Record<string, File> = {};
        const urlsToPass: Record<string, string> = {};
        Object.entries(uploadedFiles).forEach(([name, state]: [string, UploadedFileState]) => {
            if (state.status === 'verified') {
                if (state.file) filesToPass[name] = state.file;
                if (state.url) urlsToPass[name] = state.url;
            }
        });
        onComplete(filesToPass, urlsToPass);
    };

    const getStatusIndicator = (state: UploadedFileState) => {
        switch (state.status) {
            case 'verifying': return <div className="flex items-center text-xs text-blue-600"><SpinnerIcon className="w-4 h-4 mr-1 animate-spin" /> Analyzing & Extracting Data...</div>;
            case 'uploading': return <div className="flex items-center text-xs text-blue-600"><SpinnerIcon className="w-4 h-4 mr-1 animate-spin" /> Saving to Cloud...</div>;
            case 'verified': 
                return (
                    <div className="flex flex-col items-start">
                        <div className="flex items-center text-xs text-green-600 font-bold">
                            <CheckCircleIcon className="w-5 h-5 mr-1" /> Verified
                        </div>
                        {state.extractedCount ? (
                            <div className="flex items-center text-xs text-purple-600 mt-1 font-medium bg-purple-50 px-2 py-0.5 rounded-full">
                                <SparkleIcon className="w-3 h-3 mr-1" /> {state.extractedCount} fields auto-filled
                            </div>
                        ) : null}
                    </div>
                );
            case 'error': return <XCircleIcon className="w-6 h-6 text-red-500" />;
            default: return null;
        }
    };
    
    if (allDocs.length === 0) {
        return (
            <div className="text-center p-8 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-gray-800">No Documents Required</h3>
                <button onClick={() => onComplete({}, {})} className="mt-6 bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-8 rounded-full shadow-lg">
                    Next: Final Questionnaire <ArrowRightIcon className="w-5 h-5 inline ml-2" />
                </button>
            </div>
        );
    }

    return (
        <div>
            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6">
                <h3 className="text-lg font-bold text-blue-900 flex items-center gap-2">
                    <ShieldIcon className="w-5 h-5" /> Secure Auto-Save & Extraction
                </h3>
                <p className="text-blue-800 text-sm mt-1">
                    We automatically extract information from your documents (like SSN and Employer EIN) to fill out the forms for you. Documents are encrypted and saved securely.
                </p>
            </div>

            <h3 className="text-2xl font-bold text-gray-900 mb-2">Step 1: Upload Your Documents</h3>
            <div className="space-y-4">
                {allDocs.map(doc => {
                    const docState = uploadedFiles[doc.name] || { status: 'idle' };
                    return (
                        <div key={doc.name} className={`bg-gray-50 border rounded-lg p-4 transition-all ${docState?.status === 'error' ? 'border-red-300 bg-red-50' : 'border-gray-200'}`}>
                            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                                <div className="flex-1">
                                    <p className="font-semibold text-gray-800">{doc.name}</p>
                                    <p className="text-sm text-gray-500 mb-1">{doc.description}</p>
                                    {getStatusIndicator(docState)}
                                </div>
                                <label className={`relative cursor-pointer font-semibold py-2 px-4 rounded-lg transition-colors text-sm w-full sm:w-auto text-center ${
                                    docState?.status === 'verified' ? 'bg-green-100 text-green-700 border border-green-200' : 'bg-white border border-gray-300 hover:bg-gray-100'
                                }`}>
                                    { docState?.status === 'verified' ? 'Replace File' : 'Upload File' }
                                    <input type="file" className="hidden" accept="image/png, image/jpeg, application/pdf" onChange={(e) => e.target.files && handleFileUpload(doc.name, e.target.files[0])} disabled={docState?.status === 'verifying' || docState?.status === 'uploading'} />
                                </label>
                            </div>
                            {docState?.status === 'error' && <p className="text-sm text-red-600 font-medium mt-2">{docState.error}</p>}
                        </div>
                    );
                })}
            </div>
            <button onClick={handleNext} disabled={!allDocsVerified} className="mt-8 w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-8 rounded-full shadow-lg transition-all duration-300 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                Next: Final Questionnaire <ArrowRightIcon className="w-5 h-5" />
            </button>
        </div>
    );
};

const FinalQuestionnaireStep: React.FC<{ 
    onComplete: (data: Record<string, any>) => void; 
    programs: ForgivenessProgram[]; 
    initialData?: Record<string, any>;
    onAutoSave: (data: Record<string, any>) => void;
}> = ({ onComplete, programs, initialData, onAutoSave }) => {
    const [formData, setFormData] = useState<Record<string, any>>(initialData || {});
    const [saving, setSaving] = useState(false);
    
    // Auto-save debouncer
    useEffect(() => {
        const handler = setTimeout(() => {
            if (Object.keys(formData).length > 0) {
                setSaving(true);
                onAutoSave(formData);
                setTimeout(() => setSaving(false), 500); // Visual delay
            }
        }, 1000); // Save 1s after last keystroke

        return () => clearTimeout(handler);
    }, [formData, onAutoSave]);

    // Update if initialData changes (e.g., from doc extraction)
    useEffect(() => {
        if (initialData) {
            setFormData(prev => ({ ...prev, ...initialData }));
        }
    }, [initialData]);

    const requiredFieldNames = useMemo(() => {
        const fieldSet = new Set<string>();
        programs.forEach(p => {
            programRequirements[p.id]?.fields.forEach(fieldName => fieldSet.add(fieldName));
        });
        return Array.from(fieldSet);
    }, [programs]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;
        const isCheckbox = type === 'checkbox';
        setFormData(prev => ({ ...prev, [name]: isCheckbox ? (e.target as HTMLInputElement).checked : value }));
    };
    
    const isFormValid = useMemo(() => {
        return requiredFieldNames.every(fieldName => {
            const fieldConfig = allFieldsConfig[fieldName];
            if (!fieldConfig.required) return true;
            if (fieldName === 'spouse_agi' && formData['marital_status'] !== 'married') return true;
            const value = formData[fieldName];
            if (value === undefined || value === '' || (fieldConfig.type === 'checkbox' && value === false)) return false;
            if (fieldName === 'ssn') {
                const ssnWithoutDashes = String(value).replace(/-/g, '');
                if (!/^\d{9}$/.test(ssnWithoutDashes)) return false;
            }
            return true;
        });
    }, [formData, requiredFieldNames]);

    return (
        <div>
            <div className="flex justify-between items-center mb-2">
                <h3 className="text-2xl font-bold text-gray-900">Step 2: Final Details</h3>
                {saving ? (
                    <span className="text-xs text-blue-600 font-medium animate-pulse">Auto-saving...</span>
                ) : (
                    <span className="text-xs text-gray-400">All changes saved</span>
                )}
            </div>
            <p className="text-gray-600 mb-6">Review the details extracted from your documents. These fields map directly to the official government forms.</p>
            <form onSubmit={(e) => { e.preventDefault(); onComplete(formData); }} className="space-y-4 bg-gray-50 p-6 rounded-lg border">
                {requiredFieldNames.map(fieldName => {
                    const config = allFieldsConfig[fieldName];
                    if (fieldName === 'spouse_agi' && formData['marital_status'] !== 'married') return null;
                    
                    let isInvalid = false;
                    let errorMessage = '';
                    if (fieldName === 'ssn' && formData.ssn) {
                        const ssnValue = String(formData.ssn);
                        const ssnDigits = ssnValue.replace(/-/g, '');
                        if (/[^\d]/.test(ssnDigits)) { isInvalid = true; errorMessage = 'Numbers only.'; } 
                        else if (ssnDigits.length > 9) { isInvalid = true; errorMessage = 'Max 9 digits.'; }
                    }

                    if (config.type === 'checkbox') {
                        return (
                            <label key={fieldName} className="flex items-center gap-3 p-3 bg-white rounded-md border">
                                <input type="checkbox" name={fieldName} checked={!!formData[fieldName]} onChange={handleInputChange} className="h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                                <span className="text-gray-700 font-medium">{config.label}</span>
                            </label>
                        )
                    }

                    return (
                        <label key={fieldName} className="block group">
                            <div className="flex justify-between">
                                <span className="text-gray-700 font-semibold">{config.label} {config.required && <span className="text-red-500">*</span>}</span>
                                {initialData && initialData[fieldName] && (
                                    <span className="text-[10px] uppercase font-bold text-purple-600 bg-purple-50 px-2 py-0.5 rounded-full flex items-center">
                                        <SparkleIcon className="w-3 h-3 mr-1"/> Auto-filled from Document
                                    </span>
                                )}
                            </div>
                            {config.type === 'select' ? (
                                <select name={fieldName} value={formData[fieldName] || ''} onChange={handleInputChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 transition-colors">
                                    <option value="" disabled>Select...</option>
                                    {config.options?.map(opt => <option key={opt.value} value={opt.label}>{opt.label}</option>)}
                                </select>
                            ) : (
                                <input 
                                    type={config.type} 
                                    name={fieldName} 
                                    value={formData[fieldName] || ''} 
                                    onChange={handleInputChange} 
                                    placeholder={config.placeholder} 
                                    className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 transition-colors ${isInvalid ? 'border-red-500' : ''}`}
                                />
                            )}
                            {isInvalid && <span className="text-xs text-red-500">{errorMessage}</span>}
                        </label>
                    );
                })}

                <button type="submit" disabled={!isFormValid} className="w-full mt-6 bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-8 rounded-full shadow-lg transition-all duration-300 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                   <SparkleIcon className="w-5 h-5" /> Generate & Submit Applications
                </button>
            </form>
        </div>
    );
};

// --- Main Process Container ---

const ApplicationProcess: React.FC<{
    userData: UserData;
    onComplete: () => void;
    programs: ForgivenessProgram[];
    onSubmissionComplete: (programIds: string[], data: Record<string, any>, documents: Record<string, File>) => void;
    isTestMode: boolean;
    userId: string;
    clientId: string;
    existingDraft?: DraftApplication;
}> = ({ userData, onComplete, programs, onSubmissionComplete, isTestMode, userId, clientId, existingDraft }) => {
    
    const [step, setStep] = useState<'documents' | 'questionnaire'>(existingDraft?.step || 'documents');
    
    // State to hold data between steps
    const [collectedFiles, setCollectedFiles] = useState<Record<string, File>>({});
    const [collectedUrls, setCollectedUrls] = useState<Record<string, string>>(existingDraft?.uploadedFiles || {});
    const [collectedFormData, setCollectedFormData] = useState<Record<string, any>>(existingDraft?.formData || {});

    // Save Draft Helper
    const updateDraft = async (newStep: 'documents' | 'questionnaire', files: Record<string, string>, data: Record<string, any>) => {
        await saveDraft(userId, clientId, {
            step: newStep,
            uploadedFiles: files,
            formData: data,
            lastUpdated: new Date().toISOString()
        });
    };

    const handleDocsComplete = (files: Record<string, File>, urls: Record<string, string>) => {
        const mergedUrls = { ...collectedUrls, ...urls };
        setCollectedFiles(prev => ({ ...prev, ...files }));
        setCollectedUrls(mergedUrls);
        setStep('questionnaire');
        updateDraft('questionnaire', mergedUrls, collectedFormData);
    };

    const handleDocsAutoSave = (urls: Record<string, string>) => {
        const mergedUrls = { ...collectedUrls, ...urls };
        setCollectedUrls(mergedUrls);
        updateDraft('documents', mergedUrls, collectedFormData);
    };

    const handleDataExtracted = (data: Record<string, any>) => {
        // Merge extracted data into form data state
        const merged = { ...collectedFormData, ...data };
        setCollectedFormData(merged);
        // We also save draft here so extraction isn't lost if they reload
        updateDraft('documents', collectedUrls, merged);
    };

    const handleQuestionnaireComplete = (data: Record<string, any>) => {
        // Final Merge
        const finalData = { ...collectedFormData, ...data };
        
        // Trigger Submission in Parent
        const programIds = programs.map(p => p.id);
        onSubmissionComplete(programIds, finalData, collectedFiles);
        
        // Clear draft effectively by marking complete (handled by parent logic typically, or we could delete the draft doc here)
        // For now, we just call onComplete to close view
        onComplete();
    };

    const handleQuestionnaireAutoSave = (data: Record<string, any>) => {
        const mergedData = { ...collectedFormData, ...data };
        setCollectedFormData(mergedData);
        updateDraft('questionnaire', collectedUrls, mergedData);
    };

    return (
        <div className="bg-white p-6 md:p-8 rounded-2xl shadow-sm border border-gray-100">
            {step === 'documents' && (
                <DocumentUploadStep 
                    programs={programs}
                    onComplete={handleDocsComplete}
                    onAutoSave={handleDocsAutoSave}
                    onDataExtracted={handleDataExtracted}
                    isTestMode={isTestMode}
                    initialData={collectedUrls}
                    userId={userId}
                    clientId={clientId}
                />
            )}
            {step === 'questionnaire' && (
                <FinalQuestionnaireStep 
                    programs={programs}
                    onComplete={handleQuestionnaireComplete}
                    onAutoSave={handleQuestionnaireAutoSave}
                    initialData={collectedFormData}
                />
            )}
        </div>
    );
};

export default ApplicationProcess;
